import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { ArrowLeftIcon, XMarkIcon, BanknotesIcon, DevicePhoneMobileIcon, CreditCardIcon, UserIcon, GiftIcon, InformationCircleIcon, TagIcon, HeartIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../Utils';
import CartContext from '../../Context/Cart/CartContext';
import { useAddOrder, GetActiveCampaigns } from '../../hooks/reactQuery';
import { useMerchantSettings } from '../../hooks/reactQuery/useMerchant';
import { useCustomerLookup } from '../../hooks/reactQuery/useCustomer';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../Context/Brand/BrandContext';
import { createShoutout, getShoutoutAllowance } from '../../services/shoutout';
import UgandaPhoneInput from '../shared/UgandaPhoneInput';
import { usePlatformBrand } from '../../Context/PlatformBrand/PlatformBrandContext';

const PAYMENT_METHODS_MAP = {
  cash: {
    id: 'cash',
    title: 'Cash',
    Icon: BanknotesIcon,
    desc: 'Pay at table',
    details: 'Send the order now and pay the restaurant team at your table.',
    badge: 'At table',
  },
  mobile_money: {
    id: 'mobile_money',
    title: 'Mobile Money',
    Icon: DevicePhoneMobileIcon,
    desc: 'MTN, Airtel, etc.',
    details: 'Mobile Money payments are not available yet.',
    badge: 'Soon',
  },
  card: {
    id: 'card',
    title: 'Card',
    Icon: CreditCardIcon,
    desc: 'Credit or debit card',
    details: 'Send the order now and pay by card when the team brings the terminal.',
    badge: 'At table',
  },
};

export default function PaymentDrawer({ onClose, onSuccess, table, merchantId, merchantName, orderType = 'dine_in', embedded = false }) {
  const platformBrand = usePlatformBrand();
  const { brand } = useBrand();
  const accentColor = brand.primaryColor || '#1A7F6D';
  const [activePayment, setActivePayment] = useState('cash');
  const [customerContact, setCustomerContact] = useState(localStorage.getItem('customer_phone') || '');
  const [guestName, setGuestName] = useState(localStorage.getItem('customer_name') || '');
  const [loading, setLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [orderRecord, setOrderRecord] = useState(null);
  const [orderedItems, setOrderedItems] = useState([]);
  const [shoutoutMessage, setShoutoutMessage] = useState('');
  const [shoutoutRating, setShoutoutRating] = useState(5);
  const [shoutout, setShoutout] = useState(null);
  const [shoutoutAllowance, setShoutoutAllowance] = useState(null);
  const [loadingShoutoutAllowance, setLoadingShoutoutAllowance] = useState(false);
  const [creatingShoutout, setCreatingShoutout] = useState(false);
  const [usePoints, setUsePoints] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const { cartItems, total, clearCart } = useContext(CartContext);
  const { mutateAsync: placeOrder } = useAddOrder();
  const { data: settings, isLoading: loadingSettings } = useMerchantSettings(merchantId);
  const navigate = useNavigate();
  const drawerRef = useRef(null);
  const orderIdempotencyRef = useRef(null);

  const customerName = localStorage.getItem('customer_name') || '';
  const customerPhone = localStorage.getItem('customer_phone') || '';
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const hasAccountSession = !!(localStorage.getItem('tokens') && storedUser?.id);
  const isGuest = !hasAccountSession;
  const lookupPhone = isGuest ? customerContact : customerPhone;

  const { data: customer } = useCustomerLookup(merchantId, lookupPhone || null);
  const rewardsEnabled = settings?.rewards_enabled !== false;
  const guestPointsEnabled = settings?.guest_points_enabled !== false;
  const canEarnShoutoutPoints = rewardsEnabled && (!isGuest || guestPointsEnabled);
  const isAccountCustomer = !!(hasAccountSession && customer?.user_id && customer.user_id === storedUser?.id);
  const canUseRewards = rewardsEnabled && isAccountCustomer;
  const pointsBalance = canUseRewards ? (customer?.total_points || 0) : 0;
  const guestPointsBalance = isGuest ? (customer?.total_points || 0) : 0;
  const rawPointValue = Number(settings?.point_value_shillings);
  const hasPointValueSetting = settings?.point_value_shillings !== undefined
    && settings?.point_value_shillings !== null
    && Number.isFinite(rawPointValue)
    && rawPointValue > 0;
  const pointValueShillings = hasPointValueSetting ? rawPointValue : 0;
  const maxDiscountablePoints = pointValueShillings > 0 ? Math.ceil(total / pointValueShillings) : 0;
  const redeemablePoints = Math.min(pointsBalance, maxDiscountablePoints);
  const pointsDiscount = canUseRewards && usePoints && hasPointValueSetting
    ? Math.min(total, Math.floor(redeemablePoints * pointValueShillings))
    : 0;

  const { data: allCampaigns = [] } = GetActiveCampaigns(merchantId);
  const eligibleCampaigns = allCampaigns.filter(c => total >= (c.min_order_amount || 0));

  const getCampaignDiscount = (campaign) => {
    if (!campaign) return 0;
    if (campaign.discount_type === 'percentage') return Math.round(total * campaign.discount_value / 100);
    if (campaign.discount_type === 'fixed_amount') return Math.min(total, campaign.discount_value);
    return 0;
  };

  const campaignDiscount = getCampaignDiscount(selectedCampaign);
  const finalTotal = Math.max(0, total - pointsDiscount - campaignDiscount);
  const merchantPaymentMethods = settings?.payment_methods || ['cash', 'mobile_money'];
  const availableMethods = merchantPaymentMethods.map(id => PAYMENT_METHODS_MAP[id]).filter(Boolean);
  const shoutoutSharePoints = Math.max(0, Number(settings?.shoutout_share_points ?? Math.min(Number(settings?.shoutout_points ?? 25), 10)));
  const shoutoutOrderPoints = Math.max(0, Number(settings?.shoutout_order_points ?? settings?.shoutout_points ?? 25));
  const hasExistingShoutout = !!(shoutout?.shareUrl || shoutoutAllowance?.hasExistingShoutout);
  const canShareShoutout = hasExistingShoutout || !shoutoutAllowance || shoutoutAllowance.canShare;
  const shoutoutLimitReached = !!(shoutoutAllowance && !shoutoutAllowance.canShare && !hasExistingShoutout);

  const getPaymentMethodCopy = (method) => {
    if (!method) return null;
    if (orderType !== 'takeaway') return method;

    if (method.id === 'cash') {
      return {
        ...method,
        desc: 'Pay on collection',
        details: 'Send the order now and pay when you collect it.',
        badge: 'On collection',
      };
    }

    if (method.id === 'card') {
      return {
        ...method,
        desc: 'Pay on collection',
        details: 'Send the order now and pay by card when you collect it.',
        badge: 'On collection',
      };
    }

    return method;
  };

  useEffect(() => {
    if (availableMethods.length > 0 && !availableMethods.find(m => m.id === activePayment)) {
      setActivePayment(availableMethods[0].id);
    }
  }, [availableMethods, activePayment]);

  useEffect(() => {
    if (embedded) return undefined;
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [embedded, onClose]);

  const loadShoutoutAllowance = useCallback(async (createdOrder) => {
    const customerId = createdOrder?.customer_id || customer?.id;
    if (!paymentSuccess || !merchantId || !createdOrder?.id || !customerId) {
      setLoadingShoutoutAllowance(false);
      return;
    }

    setLoadingShoutoutAllowance(true);
    try {
      const res = await getShoutoutAllowance({
        restaurant_id: merchantId,
        order_id: createdOrder.id,
        customer_id: customerId,
      });

      if (!res?.success) throw new Error(res?.error || 'Could not load Shout-Out allowance');

      const allowance = res?.data?.allowance || null;
      setShoutoutAllowance(allowance);
      if (allowance?.existingShoutout?.shared_url) {
        setShoutout({
          shoutout: allowance.existingShoutout,
          shareUrl: allowance.existingShoutout.shared_url,
          allowance,
        });
      }
    } catch (error) {
      console.warn('Could not load Shout-Out allowance:', error?.message || error);
    } finally {
      setLoadingShoutoutAllowance(false);
    }
  }, [customer?.id, merchantId, paymentSuccess]);

  useEffect(() => {
    if (paymentSuccess && orderRecord?.id) {
      loadShoutoutAllowance(orderRecord);
    }
  }, [loadShoutoutAllowance, orderRecord, paymentSuccess]);

  const handlePayment = async () => {
    const cleanContact = (customerContact || '').replace(/\D/g, '');
    if (!merchantId) {
      toast.error('Missing restaurant information');
      return;
    }
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    const isValidPhone = cleanContact.startsWith('256') && cleanContact.length === 12;
    if (isGuest && (!guestName.trim() || !isValidPhone)) {
      toast.error('Enter your name and 9 digits after the country code.');
      return;
    }
    if (usePoints && !canUseRewards) {
      toast.error('Please sign in with an account to redeem points.');
      setUsePoints(false);
      return;
    }
    if (usePoints && !hasPointValueSetting) {
      toast.error('This restaurant has not set a point redemption value yet.');
      setUsePoints(false);
      return;
    }

    setLoading(true);
    try {
      const items = cartItems.map(item => ({
        menu_item_id: item.id,
        item_name: item.item_name || item.name,
        quantity: item.quantity,
        unit_price: Math.round(item.price),
        total_price: Math.round(item.price * item.quantity),
      }));

      const isTakeaway = orderType === 'takeaway';
      const attributedShoutoutId = localStorage.getItem('shoutout_id') || undefined;
      const attributedReferralCode = localStorage.getItem('referral_code') || undefined;
      const res = await placeOrder({
        idempotency_key: orderIdempotencyRef.current || (orderIdempotencyRef.current = crypto.randomUUID()),
        restaurant_id: merchantId,
        table_id: isTakeaway ? undefined : (table || undefined),
        order_type: isTakeaway ? 'takeaway' : 'dine_in',
        order_source: isTakeaway || table === 'Online' || table === '0' ? 'website' : 'qr_menu',
        customer_name: isGuest ? guestName.trim() : (customerName || 'Guest'),
        customer_phone: isGuest ? cleanContact : customerPhone,
        marketing_opt_in: marketingOptIn || undefined,
        items,
        total_amount: finalTotal,
        payment_method: activePayment,
        notes: [
          usePoints ? `Redeemed ${redeemablePoints} points for ${formatCurrency(pointsDiscount)}` : '',
          selectedCampaign ? `Campaign: ${selectedCampaign.name}` : '',
          isTakeaway ? 'Takeaway order' : '',
        ].filter(Boolean).join(' | ') || '',
        referred_by: attributedReferralCode,
        shoutout_id: attributedShoutoutId,
        points_redeemed: canUseRewards && usePoints ? redeemablePoints : 0,
        customer_id: canUseRewards ? customer?.id : undefined,
        ...(selectedCampaign ? {
          campaign_discount: {
            campaign_id: selectedCampaign.id,
            campaign_name: selectedCampaign.name,
            discount_type: selectedCampaign.discount_type,
            discount_value: selectedCampaign.discount_value,
            amount: campaignDiscount,
          }
        } : {}),
      });

      if (!res?.success) throw new Error(res?.error || 'Order creation failed. Please try again.');
      if (attributedShoutoutId) {
        localStorage.removeItem('shoutout_id');
        localStorage.removeItem('referral_code');
      }
      orderIdempotencyRef.current = null;
      const orderNum = res?.data?.order?.order_number || res?.data?.order_number;
      if (!orderNum) throw new Error('Order created but order number was not received.');

      setOrderNumber(orderNum);
      const createdOrder = res?.data?.order || null;
      setOrderRecord(createdOrder);
      if (createdOrder?.customer_id) {
        localStorage.setItem('customer_id', createdOrder.customer_id);
      }
      if (merchantId) {
        localStorage.setItem('merchant_id', merchantId);
      }
      const savedPhone = isGuest ? cleanContact : customerPhone;
      if (savedPhone) {
        localStorage.setItem('customer_phone', savedPhone);
      }
      const savedName = isGuest ? guestName.trim() : customerName;
      if (savedName) {
        localStorage.setItem('customer_name', savedName);
      }
      setOrderedItems(cartItems);
      setShoutoutMessage('');
      setShoutoutRating(5);
      setShoutout(null);
      setShoutoutAllowance(null);
      setLoadingShoutoutAllowance(true);
      setPaymentSuccess(true);
      clearCart();
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.message || 'Could not place the order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const suggestedShoutout = () => {
    const firstItem = orderedItems.find((item) => item?.item_name || item?.name);
    const itemName = firstItem?.item_name || firstItem?.name;
    return itemName ? `${itemName} deserves a try.` : `${merchantName || 'This restaurant'} is worth discovering.`;
  };

  const handleCreateShoutout = async (shareType = 'link') => {
    if (!orderRecord?.id) {
      toast.error('Order details are still loading. Try again in a moment.');
      return;
    }
    if (shoutoutLimitReached) {
      toast.error(`Daily Shout-Out limit reached. You can give up to ${shoutoutAllowance.maxDaily} per day.`);
      return null;
    }

    setCreatingShoutout(true);
    try {
      const res = await createShoutout({
        restaurant_id: merchantId,
        order_id: orderRecord.id,
        customer_id: orderRecord.customer_id || customer?.id || undefined,
        message: shoutoutMessage || suggestedShoutout(),
        rating: shoutoutRating,
        share_type: shareType,
        origin: window.location.origin,
      });

      if (!res?.success) throw new Error(res?.error || 'Could not create Shout-Out');
      setShoutout(res.data);
      if (res?.data?.allowance) setShoutoutAllowance(res.data.allowance);
      const awarded = Number(res?.data?.pointsAwarded || res?.data?.shoutout?.points_awarded || 0);
      toast.success(awarded > 0 ? `Shout-Out ready. ${awarded.toLocaleString()} points added.` : 'Shout-Out ready');
      return res.data;
    } catch (error) {
      toast.error(error?.message || 'Could not create Shout-Out');
      return null;
    } finally {
      setCreatingShoutout(false);
    }
  };

  const getOrCreateShoutout = async (shareType) => shoutout || handleCreateShoutout(shareType);

  const handleShareWhatsApp = async () => {
    const shareWindow = window.open('', '_blank');
    const result = await getOrCreateShoutout('whatsapp');
    const shareUrl = result?.shareUrl;
    if (!shareUrl) {
      shareWindow?.close();
      return;
    }

    const name = (isGuest ? guestName : customerName) || 'I';
    const msg = `${name} gave a Shout-Out to ${merchantName || 'this local restaurant'}.\n\n${shoutoutMessage || suggestedShoutout()}\n\nDiscover it here:\n${shareUrl}\n\nShared via ${platformBrand.name}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    if (shareWindow) shareWindow.location.href = whatsappUrl;
    else window.location.href = whatsappUrl;
  };

  const handleCopyLink = async () => {
    const result = await getOrCreateShoutout('copy');
    const shareUrl = result?.shareUrl;
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Shout-Out link copied');
    } catch {
      const input = document.createElement('textarea');
      input.value = shareUrl;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      input.remove();
      if (copied) toast.success('Shout-Out link copied');
      else toast.error('Could not copy the link. Please try again.');
    }
  };

  const handleDone = () => {
    if (onSuccess) onSuccess(orderNumber);
    else navigate('/my-account/orders', { state: { orderId: orderNumber } });
  };

  const renderPaymentMethod = (method) => {
    const displayMethod = getPaymentMethodCopy(method);
    const isActive = activePayment === method.id;
    return (
      <button
        key={method.id}
        onClick={() => setActivePayment(method.id)}
        className={`flex items-center gap-3 w-full p-3.5 rounded-xl border transition-all ${
          isActive ? 'bg-[var(--brand-primary)]/5' : 'border-gray-100 bg-white hover:border-gray-200'
        }`}
        style={isActive ? { borderColor: accentColor } : undefined}
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? 'text-white' : 'bg-gray-100 text-gray-500'}`} style={isActive ? { backgroundColor: accentColor } : undefined}>
          <method.Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-semibold text-gray-900">{displayMethod.title}</p>
          <p className="text-xs mt-0.5 text-gray-500">{displayMethod.desc}</p>
        </div>
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
          {displayMethod.badge}
        </span>
      </button>
    );
  };

  return (
    <div className={embedded ? 'flex min-h-0 flex-1 flex-col bg-white' : 'fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center'}>
      <div ref={drawerRef} className={embedded ? 'flex h-full min-h-0 w-full flex-col bg-white' : 'flex max-h-[90vh] w-full max-w-md flex-col rounded-t-2xl bg-white shadow-xl sm:rounded-2xl'}>
        {loading ? (
          <div className="p-6 text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: accentColor }} />
            <p className="text-gray-500 text-sm">Sending your order...</p>
          </div>
        ) : paymentSuccess ? (
          <div className="p-5 py-6 space-y-4 overflow-y-auto thin-scroll">
            <div className="flex items-center gap-3 rounded-2xl bg-gray-50 px-4 py-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white">
                <svg className="w-6 h-6" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-950">Order sent</h2>
                <p className="text-sm text-gray-500">The restaurant has received order <span className="font-mono font-semibold text-gray-800">{orderNumber}</span>.</p>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-sm">
              <div className="flex items-start gap-3 border-b border-gray-100 p-4">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: `${accentColor}12` }}>
                  <HeartIcon className="h-5 w-5" style={{ color: accentColor }} />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">Did you enjoy your meal?</p>
                  <h3 className="mt-1 text-xl font-semibold leading-6 text-gray-950">Who deserves to know about this place?</h3>
                  <p className="mt-2 text-sm leading-5 text-gray-500">
                    Share a simple Shout-Out for {merchantName || 'this restaurant'} and help someone discover it.
                  </p>
                </div>
              </div>

              <div className="space-y-4 p-4">
                <div className="rounded-2xl bg-gray-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-500">{(isGuest ? guestName : customerName) || 'You'} recommends</p>
                      <p className="mt-1 text-lg font-semibold leading-6 text-gray-950">{merchantName || 'this local restaurant'}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-700">
                      {shoutoutRating}/5
                    </span>
                  </div>
                <p className="mt-4 text-sm font-semibold text-gray-800">How was it?</p>
                <div className="mt-2 flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setShoutoutRating(value)}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-xl leading-none transition active:scale-95"
                      style={{ color: value <= shoutoutRating ? accentColor : '#d1d5db' }}
                      aria-label={`${value} star rating`}
                    >
                      &#9733;
                    </button>
                  ))}
                </div>
                <div className="mt-4">
                  <textarea
                    value={shoutoutMessage}
                    onChange={(event) => setShoutoutMessage(event.target.value.slice(0, 180))}
                    placeholder={suggestedShoutout()}
                    className="min-h-[92px] w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm leading-5 text-gray-900 outline-none placeholder:text-gray-400 focus:border-gray-400"
                  />
                  <div className="mt-2 flex items-center justify-between gap-3 text-xs text-gray-400">
                    <span>Shared via {platformBrand.name}.</span>
                    <span>{shoutoutMessage.length}/180</span>
                  </div>
                </div>
                {canEarnShoutoutPoints && (shoutoutSharePoints > 0 || shoutoutOrderPoints > 0) && (
                  <div className="mt-3 rounded-xl bg-white px-3 py-2 text-xs leading-5 text-gray-600">
                    {shoutoutSharePoints > 0 && (
                      <span>You receive <span className="font-semibold text-gray-900">{shoutoutSharePoints.toLocaleString()} points</span> when your Shout-Out is created.</span>
                    )}
                    {shoutoutOrderPoints > 0 && (
                      <span className={shoutoutSharePoints > 0 ? 'block mt-1' : ''}>
                        Earn <span className="font-semibold text-gray-900">{shoutoutOrderPoints.toLocaleString()} points</span> after a new customer's first order from your link is completed and paid.
                      </span>
                    )}
                  </div>
                )}
                <div className={`mt-3 rounded-xl px-3 py-2 text-xs leading-5 ${
                  shoutoutLimitReached
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-white text-gray-600'
                }`}>
                  {loadingShoutoutAllowance ? (
                    'Checking today’s Shout-Out limit...'
                  ) : hasExistingShoutout ? (
                    'A Shout-Out is already ready for this order. You can keep sharing this link.'
                  ) : shoutoutAllowance ? (
                    shoutoutAllowance.remaining > 0
                      ? `You can create ${shoutoutAllowance.remaining} more Shout-Out${shoutoutAllowance.remaining === 1 ? '' : 's'} today.`
                      : `Daily Shout-Out limit reached. You can give up to ${shoutoutAllowance.maxDaily} per day.`
                  ) : (
                    'Restaurants can limit how many Shout-Outs each customer creates per day.'
                  )}
                </div>
              </div>

              {shoutout?.shareUrl && (
                <div className="mt-3 rounded-xl bg-gray-50 px-3 py-2">
                  <p className="truncate text-xs text-gray-500">{shoutout.shareUrl}</p>
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  onClick={handleShareWhatsApp}
                  disabled={creatingShoutout || loadingShoutoutAllowance || !canShareShoutout}
                  className="rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ backgroundColor: accentColor }}
                >
                  {creatingShoutout ? 'Preparing...' : 'WhatsApp'}
                </button>
                <button
                  onClick={handleCopyLink}
                  disabled={creatingShoutout || loadingShoutoutAllowance || !canShareShoutout}
                  className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-800 disabled:opacity-60"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy link
                </button>
              </div>
            </div>
            </div>

            <button onClick={handleDone} className="w-full px-4 py-3 text-white rounded-xl font-semibold transition-colors" style={{ backgroundColor: accentColor }}>
              {shoutout ? 'View order' : 'Not now'}
            </button>
          </div>
        ) : (
          <>
            <div className="flex h-16 shrink-0 items-center gap-3 border-b border-gray-100 px-4 sm:px-5">
              <button onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-gray-600 transition-colors hover:bg-gray-100" aria-label={embedded ? 'Back to order summary' : 'Close checkout'}>
                {embedded ? <ArrowLeftIcon className="h-5 w-5" /> : <XMarkIcon className="h-5 w-5" />}
              </button>
              <div className="min-w-0">
                <h2 className="text-[17px] font-semibold text-gray-900">Checkout</h2>
                {embedded && <p className="truncate text-[12px] text-gray-500">Review your details and place the order</p>}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto thin-scroll px-5 py-4 space-y-4">
              <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3.5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${accentColor}15` }}>
                    <UserIcon className="w-5 h-5" style={{ color: accentColor }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{isGuest ? (guestName || customerName || 'Guest') : customerName}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {orderType === 'takeaway' ? 'Takeaway order' : (isGuest ? 'Guest order' : `Table ${table || '-'}`)}
                      {merchantName ? ` · ${merchantName}` : ''}
                    </p>
                  </div>
                </div>
                {canUseRewards && (
                  <span className="text-xs bg-amber-100 text-amber-700 rounded-full px-2.5 py-1 font-medium flex items-center gap-1">
                    <GiftIcon className="w-3 h-3" />
                    {pointsBalance} pts
                  </span>
                )}
              </div>

              {canUseRewards && pointsBalance > 0 && hasPointValueSetting && pointValueShillings > 0 && (
                <div className="bg-amber-50 rounded-xl p-3.5 border border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-amber-800">Points</p>
                    <span className="text-sm font-bold text-amber-700">{pointsBalance} pts</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={usePoints}
                      onChange={(e) => setUsePoints(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-xs text-amber-700">
                      Redeem {redeemablePoints} pts ({formatCurrency(pointsDiscount || Math.min(total, Math.floor(redeemablePoints * pointValueShillings)))})
                    </span>
                  </label>
                  {usePoints && (
                    <p className="text-xs text-amber-600 mt-1.5 pt-1.5 border-t border-amber-200">
                      {pointValueShillings > 0
                        ? `This restaurant set 1 point = ${formatCurrency(pointValueShillings)}. Discount: ${formatCurrency(pointsDiscount)}`
                        : 'Point redemption is currently disabled.'}
                    </p>
                  )}
                </div>
              )}
              {canUseRewards && pointsBalance > 0 && !loadingSettings && !hasPointValueSetting && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-xs leading-5 text-amber-700">
                  Points are available, but this restaurant has not set their redemption value yet.
                </div>
              )}

              {isGuest && rewardsEnabled && guestPointsEnabled && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5">
                  <div className="flex items-start gap-2">
                    <GiftIcon className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">{guestPointsBalance > 0 ? `${guestPointsBalance} points saved` : 'Guest points are saved'}</p>
                      <p className="text-xs text-amber-700 mt-1">Points earned as a guest stay on this phone number. Create an account with the same phone to redeem them later.</p>
                    </div>
                  </div>
                </div>
              )}
              {isGuest && rewardsEnabled && !guestPointsEnabled && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3.5">
                  <div className="flex items-start gap-2">
                    <GiftIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Sign in to earn points</p>
                      <p className="mt-1 text-xs leading-5 text-gray-600">This restaurant awards new points to {platformBrand.name} account customers. Your order can still be placed as a guest.</p>
                    </div>
                  </div>
                </div>
              )}

              {eligibleCampaigns.length > 0 && (
                <div className="rounded-xl p-3.5 border space-y-2" style={{ backgroundColor: `${accentColor}06`, borderColor: `${accentColor}20` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <TagIcon className="w-4 h-4" style={{ color: accentColor }} />
                    <p className="text-sm font-semibold text-gray-900">Offers</p>
                  </div>
                  {eligibleCampaigns.map(campaign => {
                    const isSelected = selectedCampaign?.id === campaign.id;
                    const discountLabel = campaign.discount_type === 'percentage'
                      ? `${campaign.discount_value}% off`
                      : `${formatCurrency(campaign.discount_value)} off`;
                    return (
                      <button
                        key={campaign.id}
                        onClick={() => setSelectedCampaign(isSelected ? null : campaign)}
                        className="w-full flex items-center justify-between p-2.5 rounded-xl border transition-all text-left"
                        style={isSelected ? { borderColor: accentColor, backgroundColor: `${accentColor}0D` } : { borderColor: '#e5e7eb', backgroundColor: 'white' }}
                      >
                        <div>
                          <p className="text-xs font-semibold text-gray-800">{campaign.name}</p>
                          <p className="text-[11px] text-gray-400">{discountLabel}</p>
                        </div>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={isSelected ? { backgroundColor: accentColor, color: 'white' } : { backgroundColor: `${accentColor}15`, color: accentColor }}>
                          {isSelected ? 'Applied' : 'Apply'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {isGuest && (
                <div className="space-y-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">Contact details</p>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className="w-full h-11 px-3 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': `${accentColor}50` }}
                  />
                  <UgandaPhoneInput
                    value={customerContact}
                    onChange={setCustomerContact}
                    className="!h-11 !border-gray-200 !bg-white"
                    inputClassName="!text-sm"
                  />
                </div>
              )}

              <label className="flex cursor-pointer items-start gap-3 py-2">
                <input type="checkbox" checked={marketingOptIn} onChange={(event) => setMarketingOptIn(event.target.checked)}
                  disabled={loading} className="mt-1 h-5 w-5 shrink-0 rounded border-gray-300" style={{ accentColor }} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium leading-6 text-gray-900">Send me offers and updates from {merchantName || brand.restaurantName || 'this restaurant'}.</span>
                  <span className="mt-1 block text-xs leading-5 text-gray-500">Optional. Change your mind anytime in your account or by contacting the restaurant.</span>
                </span>
              </label>

              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">Payment method</p>
                <div className="space-y-2">{availableMethods.map(renderPaymentMethod)}</div>
                {activePayment && (
                  <div className="flex items-start gap-2 bg-blue-50 rounded-xl p-3 border border-blue-100">
                    <InformationCircleIcon className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-blue-700 leading-relaxed">{getPaymentMethodCopy(PAYMENT_METHODS_MAP[activePayment])?.details}</p>
                  </div>
                )}
              </div>

              {activePayment === 'mobile_money' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                  <p className="text-sm font-medium text-amber-800">Mobile Money is not available yet</p>
                  <p className="text-xs text-amber-600 mt-1">Please choose Cash or Card for this order.</p>
                </div>
              )}

              <div className="rounded-xl p-4 space-y-2 border" style={{ backgroundColor: `${accentColor}08`, borderColor: `${accentColor}20` }}>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(total)}</span>
                </div>
                {usePoints && pointsDiscount > 0 && (
                  <div className="flex justify-between text-sm text-amber-600">
                    <span>Points discount</span>
                    <span>-{formatCurrency(pointsDiscount)}</span>
                  </div>
                )}
                {selectedCampaign && campaignDiscount > 0 && (
                  <div className="flex justify-between text-sm" style={{ color: accentColor }}>
                    <span>{selectedCampaign.name}</span>
                    <span>-{formatCurrency(campaignDiscount)}</span>
                  </div>
                )}
                <div className="pt-2 border-t" style={{ borderColor: `${accentColor}20` }}>
                  <div className="flex justify-between font-semibold text-gray-900">
                    <span>Total</span>
                    <span style={{ color: accentColor }}>{formatCurrency(finalTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="shrink-0 border-t border-gray-100 px-5 py-3">
              <button
                onClick={handlePayment}
                disabled={activePayment === 'mobile_money'}
                className="w-full py-3.5 text-white rounded-xl font-semibold transition-colors active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: accentColor }}
              >
                {activePayment === 'mobile_money' ? 'Coming soon' : `Place order · ${formatCurrency(finalTotal)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
